import React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo/index';
import _ from 'lodash';
import {
  ScatterChart,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Scatter,
  Cell,
  CartesianGrid,
  LabelList,
} from 'recharts';
import moment from 'moment';

import AppComponent from '../components/core/AppComponent';
import Loading from '../components/core/Loading';

const DATA = [{ x: 100, y: 200, z: 200 }, { x: 120, y: 100, z: 260 },
  { x: 170, y: 300, z: 400 }, { x: 140, y: 250, z: 280 },
  { x: 150, y: 400, z: 500 }, { x: 110, y: 280, z: 200 }];
const colors = ['red', 'green', 'pink', 'yellow'];

class InsightsContainer extends AppComponent {
  parseDataFromAlerts() {
    const { facilityAlerts } = this.props.alertsQuery;

    const alertsByDay = [];
    facilityAlerts.forEach(a => {
      const timestamp = a.claimedAt;
      const label = moment(timestamp).format('ddd D');
      const index = alertsByDay.findIndex(abd => abd.label === label);
      if (index < 0) {
        alertsByDay.push({
          label,
          alerts: 1,
          unix: moment(timestamp).unix(),
        });
      } else {
        alertsByDay[index].alerts += 1;
      }
    });

    return alertsByDay.sort((a, b) => a.unix - b.unix);
  }

  parseDataForAlertsByStaff() {
    const { facilityAlerts } = this.props.alertsQuery;
    const alertsByStaff = [];

    facilityAlerts.forEach(a => {
      if (!a.confirmedBy) {
        return;
      }

      const label = a.confirmedBy.name;
      const index = alertsByStaff.findIndex(abs => abs.label === label);

      if (index < 0) {
        alertsByStaff.push({
          label,
          alertCount: 1,
          responseTimes: [moment(a.confirmedAt).diff(moment(a.claimedAt))], // todo change to created
        });
      } else {
        alertsByStaff[index].alertCount += 1;
        alertsByStaff[index].responseTimes.push(moment(a.confirmedAt).diff(moment(a.claimedAt))); // todo change to created
      }
    });

    alertsByStaff.forEach(s => {
      s.avgResponseTime = s.responseTimes.reduce((acc, curr) => acc + curr, 0) / s.responseTimes.length;
      s.avgResponseTimeLabel = moment.duration(s.avgResponseTime).humanize();
    });

    return alertsByStaff.sort((a, b) => a.avgResponseTime - b.avgResponseTime);
  }

  parseDataForAlertsByResident() {
    const { facilityAlerts } = this.props.alertsQuery;
    const alertsByResident = [];

    facilityAlerts.forEach(a => {
      const label = a.resident.name;
      const index = alertsByResident.findIndex(abs => abs.label === label);

      if (index < 0) {
        alertsByResident.push({
          label,
          alertCount: 1,
          careTimes: [moment(a.resolvedAt).diff(moment(a.confirmedAt))],
        });
      } else {
        alertsByResident[index].alertCount += 1;
        alertsByResident[index].careTimes.push(moment(a.resolvedAt).diff(moment(a.confirmedAt)));
      }
    });

    alertsByResident.forEach(s => {
      s.avgCareTime = s.careTimes.reduce((acc, curr) => acc + curr, 0) / s.careTimes.length;
      s.avgCareTimeLabel = moment.duration(s.avgCareTime).humanize();
    });

    return alertsByResident.sort((a, b) => a.avgCareTime - b.avgCareTime);
  }

  render() {
    if (this.props.alertsQuery.loading) {
      return (
        <Loading />
      );
    }

    const areaData = this.parseDataFromAlerts();
    const scatterStaffData = this.parseDataForAlertsByStaff();
    const scatterResidentData = this.parseDataForAlertsByResident();

    return (
      <React.Fragment>
        <h3 className="nightingale-text">Calls in the Last 7 Days</h3>
        <ResponsiveContainer
          width="80%"
          height={600}
        >
          <AreaChart data={areaData}>
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="alerts" stroke="#8884d8" fill="#8884d8" />
          </AreaChart>
        </ResponsiveContainer>
        <hr />
        <h3 className="nightingale-text">Response Time By Staff</h3>
        <ResponsiveContainer
          width="80%"
          height={600}
        >
          <ScatterChart>
            <XAxis
              label={{ value: 'Response Time', position: 'bottom' }}
              dataKey="avgResponseTimeLabel"
            />
            <YAxis
              label={{ value: '# of Alerts', angle: -90, position: 'insideLeft' }}
              type="number"
              dataKey="alertCount"
            />
            <CartesianGrid />
            <Tooltip />
            <Scatter data={scatterStaffData} fill="#8884d8">
              <LabelList dataKey="label" position="bottom" />
              {
                scatterStaffData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />)
              }
            </Scatter>
          </ScatterChart>

        </ResponsiveContainer>
        <hr />
        <h3 className="nightingale-text">Care Time By Resident</h3>
        <ResponsiveContainer
          width="80%"
          height={600}
        >
          <ScatterChart>
            <XAxis
              label={{ value: 'Care Time', position: 'bottom' }}
              dataKey="avgCareTimeLabel"
            />
            <YAxis
              label={{ value: '# of Alerts', angle: -90, position: 'insideLeft' }}
              type="number"
              dataKey="alertCount"
            />
            <CartesianGrid />
            <Tooltip />
            <Scatter data={scatterResidentData} fill="#8884d8">
              <LabelList dataKey="label" position="bottom" />
              {
                scatterResidentData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />)
              }
            </Scatter>
          </ScatterChart>

        </ResponsiveContainer>
      </React.Fragment>
    );
  }
}

const ALERTS_QUERY = gql`
query($id: String!, $range: DateTime!) {
  facilityAlerts(facilityId: $id, where: { createdAt_gt: $range }) {
    id
    createdAt
    claimedAt
    claimedBy {
      name
    }
    confirmedAt
    confirmedBy {
      name
    }
    resolvedAt
    resolvedBy {
      name
    }
    resident {
      name
    }
  }
}
`;

export default graphql(ALERTS_QUERY, {
  name: 'alertsQuery',
  options: props => ({
    variables: {
      id: props.facilityId,
      range: moment().subtract(7, 'd').toISOString(),
    },
  }),
})(InsightsContainer);



// WEBPACK FOOTER //
// ./src/containers/InsightsContainer.js